import { create } from "zustand";

export interface PendingPreview {
  /** Blob URL created via URL.createObjectURL — must be revoked on completion. */
  url: string;
  kind: "image" | "video";
  /** Bytes uploaded so far (XHR upload progress). 0 until first event. */
  bytesLoaded: number;
  /** Total bytes to upload. 0 if not yet known / not computable. */
  bytesTotal: number;
  /** True once this file's S3 PUT has resolved successfully. */
  done: boolean;
}

interface UploadProgress {
  totalFiles: number;
  uploadedFiles: number;
  isUploading: boolean;
  startTime: number;
  pendingPreviews: PendingPreview[];
}

export interface UploadProgressStore {
  uploads: Record<string, UploadProgress>;

  // Actions
  startUpload: (mediaSetId: string, files: File[]) => void;
  updateProgress: (mediaSetId: string, uploadedFiles: number, totalFiles: number) => void;
  updatePreviewProgress: (mediaSetId: string, index: number, bytesLoaded: number, bytesTotal: number) => void;
  markPreviewDone: (mediaSetId: string, index: number) => void;
  completeUpload: (mediaSetId: string) => void;
  clearUpload: (mediaSetId: string) => void;

  // Getters
  getUploadProgress: (mediaSetId: string) => UploadProgress | undefined;
}

const buildPendingPreviews = (files: File[]): PendingPreview[] =>
  files.map((file) => ({
    url: URL.createObjectURL(file),
    kind: file.type.startsWith("video/") ? "video" : "image",
    bytesLoaded: 0,
    bytesTotal: file.size,
    done: false,
  }));

const revokePendingPreviews = (previews: PendingPreview[] | undefined) => {
  if (!previews) return;
  for (const preview of previews) {
    URL.revokeObjectURL(preview.url);
  }
};

export const useUploadProgressStore = create<UploadProgressStore>((set, get) => ({
  uploads: {},

  startUpload: (mediaSetId: string, files: File[]) => {
    set((state) => {
      revokePendingPreviews(state.uploads[mediaSetId]?.pendingPreviews);

      // Drop settled records so completed uploads don't accumulate for the
      // whole session — only in-flight entries (and this new one) are kept.
      const activeUploads = Object.fromEntries(
        Object.entries(state.uploads).filter(([id, progress]) => id !== mediaSetId && progress.isUploading),
      );

      return {
        uploads: {
          ...activeUploads,
          [mediaSetId]: {
            totalFiles: files.length,
            uploadedFiles: 0,
            isUploading: true,
            startTime: Date.now(),
            pendingPreviews: buildPendingPreviews(files),
          },
        },
      };
    });
  },

  updateProgress: (mediaSetId: string, uploadedFiles: number, totalFiles: number) => {
    set((state) => {
      const existing = state.uploads[mediaSetId];
      if (!existing) return state;

      return {
        uploads: {
          ...state.uploads,
          [mediaSetId]: {
            ...existing,
            uploadedFiles,
            totalFiles,
          },
        },
      };
    });
  },

  updatePreviewProgress: (mediaSetId: string, index: number, bytesLoaded: number, bytesTotal: number) => {
    set((state) => {
      const existing = state.uploads[mediaSetId];
      const preview = existing?.pendingPreviews[index];
      if (!existing || !preview) return state;

      const nextPreviews = existing.pendingPreviews.slice();
      nextPreviews[index] = {
        ...preview,
        bytesLoaded,
        // Honor a known size from the XHR; fall back to the file size we recorded at startUpload.
        bytesTotal: bytesTotal > 0 ? bytesTotal : preview.bytesTotal,
      };

      return {
        uploads: {
          ...state.uploads,
          [mediaSetId]: {
            ...existing,
            pendingPreviews: nextPreviews,
          },
        },
      };
    });
  },

  markPreviewDone: (mediaSetId: string, index: number) => {
    set((state) => {
      const existing = state.uploads[mediaSetId];
      const preview = existing?.pendingPreviews[index];
      if (!existing || !preview || preview.done) return state;

      const nextPreviews = existing.pendingPreviews.slice();
      nextPreviews[index] = { ...preview, done: true };

      return {
        uploads: {
          ...state.uploads,
          [mediaSetId]: {
            ...existing,
            pendingPreviews: nextPreviews,
          },
        },
      };
    });
  },

  completeUpload: (mediaSetId: string) => {
    set((state) => {
      const existing = state.uploads[mediaSetId];
      if (!existing) return state;

      revokePendingPreviews(existing.pendingPreviews);

      return {
        uploads: {
          ...state.uploads,
          [mediaSetId]: {
            ...existing,
            isUploading: false,
            pendingPreviews: [],
          },
        },
      };
    });
  },

  clearUpload: (mediaSetId: string) => {
    set((state) => {
      revokePendingPreviews(state.uploads[mediaSetId]?.pendingPreviews);
      const { [mediaSetId]: _, ...rest } = state.uploads;
      return { uploads: rest };
    });
  },

  getUploadProgress: (mediaSetId: string) => {
    return get().uploads[mediaSetId];
  },
}));

// Selector hooks for React components
export const useUploadProgress = (mediaSetId: string) => {
  return useUploadProgressStore((state) => state.uploads[mediaSetId]);
};

export const useIsAnyFileUploading = () => {
  return useUploadProgressStore((state) => Object.values(state.uploads).some((progress) => progress.isUploading));
};

export const useActiveUploads = () => {
  return useUploadProgressStore((state) =>
    Object.entries(state.uploads)
      .filter(([_, progress]) => progress.isUploading)
      .map(([mediaSetId]) => mediaSetId),
  );
};
